import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import './UsersDropdownFilter.css';

// Build display name from known fields or email
function getDisplayName(u) {
  const fn = u.firstName || '';
  const ln = u.lastName || '';
  const name = `${fn} ${ln}`.trim();
  if (name) return name;
  const email = u.email || '';
  const local = email.split('@')[0] || '';
  return local.replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function getInitials(u) {
  const name = getDisplayName(u);
  const parts = name.split(' ').filter(Boolean);
  const first = parts[0]?.[0] || '';
  const second = parts[1]?.[0] || '';
  return (first + second).toUpperCase() || (u.email?.[0] || '?').toUpperCase();
}

export default function UsersDropdownFilter({ value = '', onChange, placeholder = 'Sélectionner un utilisateur' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  // Sources from Redux only: pictures + admin.userInfo + share.userList + projects + sessions
  const images = useSelector((s) => s.picture?.images || {});
  const sessions = useSelector((s) => s.sessions?.allSessions || []);
  const adminUsers = useSelector((s) => s.admin?.userInfo || []);
  const shareUsers = useSelector((s) => s.share?.userList || []);
  const projects = useSelector((s) => s.project?.projects || []);

  const users = useMemo(() => {
    const byEmail = new Map();
    // 1) From images dictionary (email => {img, firstName, lastName})
    Object.entries(images || {}).forEach(([email, info]) => {
      if (!email || typeof info !== 'object') return;
      if (email === 'user') return; // reserved key sometimes present
      byEmail.set(email.toLowerCase(), {
        email,
        firstName: info.firstName || '',
        lastName: info.lastName || '',
        img: info.img || null,
      });
    });
    // 2) From admin.userInfo (often the full list of accounts)
    (adminUsers || []).forEach((u) => {
      const email = (u?.email || '').trim();
      if (!email) return;
      const key = String(email).toLowerCase();
      const base = byEmail.get(key) || { email };
      byEmail.set(key, {
        email,
        firstName: u.firstName ?? base.firstName ?? '',
        lastName: u.lastName ?? base.lastName ?? '',
        img: base.img ?? null,
      });
    });
    // 3) From share.userList (team members)
    (shareUsers || []).forEach((u) => {
      const email = (u?.email || u?.userEmail || u?.id || '').trim();
      if (!email) return;
      const key = String(email).toLowerCase();
      const base = byEmail.get(key) || { email };
      byEmail.set(key, {
        email,
        firstName: u.firstName ?? base.firstName ?? '',
        lastName: u.lastName ?? base.lastName ?? '',
        img: base.img ?? null,
      });
    });
    // 4) From projects owners/creators
    (projects || []).forEach((p) => {
      const email = (p?.ownerEmail || p?.owner || p?.createdBy || '').trim();
      if (!email) return;
      const key = String(email).toLowerCase();
      if (!byEmail.has(key)) byEmail.set(key, { email, firstName: '', lastName: '', img: null });
    });
    // 5) From sessions emails (ensure we can still filter even without images)
    (sessions || []).forEach((s) => {
      const email = s?.email || s?.userId;
      if (!email) return;
      const key = String(email).toLowerCase();
      if (!byEmail.has(key)) {
        byEmail.set(key, { email, firstName: '', lastName: '', img: null });
      }
    });
    return Array.from(byEmail.values()).sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));
  }, [images, sessions, adminUsers, shareUsers, projects]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = getDisplayName(u).toLowerCase();
      return name.includes(q) || String(u.email || '').toLowerCase().includes(q);
    });
  }, [users, query]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedUser = useMemo(() => {
    if (!value) return null;
    const key = String(value).toLowerCase();
    return users.find((u) => String(u.email).toLowerCase() === key) || null;
  }, [value, users]);

  const handleSelect = (email) => {
    onChange && onChange(email);
    setOpen(false);
  };

  return (
    <div className="user-dd" ref={containerRef}>
      <div className="user-dd-control" onClick={() => setOpen((o) => !o)}>
        {selectedUser ? (
          <div className="user-dd-value">
            {selectedUser.img ? (
              <img className="user-dd-avatar" src={selectedUser.img} alt={selectedUser.email} />
            ) : (
              <div className="user-dd-avatar placeholder">{getInitials(selectedUser)}</div>
            )}
            <div className="user-dd-texts">
              <div className="user-dd-name">{getDisplayName(selectedUser)}</div>
              <div className="user-dd-email">{selectedUser.email}</div>
            </div>
          </div>
        ) : (
          <span className="user-dd-placeholder">{placeholder}</span>
        )}
        <span className="user-dd-caret">▾</span>
      </div>

      {open && (
        <div className="user-dd-menu">
          <div className="user-dd-search">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher par nom ou email"
            />
            {value && (
              <button className="user-dd-clear" onClick={(e) => { e.stopPropagation(); onChange && onChange(''); setQuery(''); }}>Effacer</button>
            )}
          </div>
          <div className="user-dd-list">
            {filtered.length === 0 && (
              <div className="user-dd-empty">Aucun utilisateur</div>
            )}
            {filtered.map((u) => (
              <div key={u.email} className="user-dd-item" onClick={() => handleSelect(u.email)}>
                {u.img ? (
                  <img className="user-dd-avatar" src={u.img} alt={u.email} />
                ) : (
                  <div className="user-dd-avatar placeholder">{getInitials(u)}</div>
                )}
                <div className="user-dd-texts">
                  <div className="user-dd-name">{getDisplayName(u)}</div>
                  <div className="user-dd-email">{u.email}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
